"use client";
// components/chat/PhoneResultCard.tsx
// Compact phone result card for embedding in chat messages.
// Displays phone info, order, job status, repairs, reports, and grades
// in a collapsible card format optimized for chat UI.

import React, { useState } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PhoneData {
  id: number;
  imei: string;
  model?: string;
  group_number?: string;
  mark?: string;
  paint_color?: string;
  original_box?: boolean | null;
  is_active?: boolean | null;
  pending?: boolean | null;
  sent_out?: boolean | null;
  sent_out_date?: string | null;
  date_scanned?: string | null;
  current_job?: string | null;
  current_job_priority?: number | null;
  order_id?: number | null;
  orders?: any | any[];
  phone_jobs_done?: any[];
  phone_jobs_expected?: any[];
  phone_jobs_done_logs?: any[];
  phone_update_log?: any[];
  repairs?: any[];
  reports?: any[];
  parts_queue?: any[];
  phone_grades?: any[];
}

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  brand:      "#01303F",
  brandMid:   "#025470",
  brandLight: "#e8f4f8",
  accent:     "#0891b2",
  bg:         "#f8fafc",
  card:       "#ffffff",
  border:     "#e2e8f0",
  borderDark: "#cbd5e1",
  text:       "#0f172a",
  textMid:    "#475569",
  textLight:  "#94a3b8",
  success:    "#059669",
  successBg:  "#d1fae5",
  warning:    "#d97706",
  warningBg:  "#fef3c7",
  danger:     "#dc2626",
  dangerBg:   "#fee2e2",
  neutral:    "#64748b",
  neutralBg:  "#f1f5f9",
  purple:     "#7c3aed",
  purpleBg:   "#ede9fe",
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function statusColor(s: string | null | undefined): { bg: string; color: string } {
  const n = (s ?? "").toLowerCase();
  if (n.includes("complet") || n === "retired" || n === "done") return { bg: C.successBg, color: C.success };
  if (n.includes("pending") || n.includes("waiting") || n.includes("in_progress")) return { bg: C.brandLight, color: C.brandMid };
  if (n.includes("paus")) return { bg: C.warningBg, color: C.warning };
  if (n.includes("damag") || n.includes("not rep") || n.includes("reject")) return { bg: C.dangerBg, color: C.danger };
  return { bg: C.neutralBg, color: C.neutral };
}

function fmtDate(d: string | null | undefined) {
  if (!d) return null;
  return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" });
}

function fmtDateShort(d: string | null | undefined) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/New_York" });
}

// ─── Design primitives ────────────────────────────────────────────────────────
function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span style={{ background: bg, color, fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 99, whiteSpace: "nowrap", display: "inline-block" }}>
      {label}
    </span>
  );
}

function KV({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.5, color: C.textLight, textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: 11, color: C.text, fontWeight: 500, fontFamily: mono ? "ui-monospace, monospace" : "inherit" }}>
        {value}
      </span>
    </div>
  );
}

function SectionHeader({ title, count, open, onToggle }: { title: string; count?: number; open: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 10px", background: open ? C.bg : "transparent", border: "none", cursor: "pointer",
        borderBottom: `1px solid ${C.border}`, borderRadius: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.brand }}>{title}</span>
        {count !== undefined && count > 0 && (
          <span style={{ fontSize: 9, fontWeight: 700, color: C.textLight, background: C.neutralBg, border: `1px solid ${C.border}`, borderRadius: 99, padding: "1px 5px" }}>
            {count}
          </span>
        )}
      </div>
      <span style={{ fontSize: 10, color: C.textLight, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</span>
    </button>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: "6px 12px" }}>
      {children}
    </div>
  );
}

// ─── Collapsible Section ──────────────────────────────────────────────────────
function Section({ title, count, children, defaultOpen = false }: { title: string; count?: number; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <SectionHeader title={title} count={count} open={open} onToggle={() => setOpen(o => !o)} />
      {open && <div style={{ padding: "8px 10px" }}>{children}</div>}
    </div>
  );
}

// ─── Order Section ────────────────────────────────────────────────────────────
function OrderInfo({ order }: { order: any }) {
  if (!order) return <span style={{ fontSize: 11, color: C.textLight }}>No order linked</span>;
  const sc = statusColor(order.status);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Row>
        <KV label="Order #" value={order.id} />
        <KV label="Company" value={order.company_name} />
        <KV label="Status" value={order.status ? <Badge label={order.status} {...sc} /> : null} />
        <KV label="Color" value={order.order_color} />
        <KV label="Received" value={fmtDateShort(order.recieved_date)} />
        <KV label="Completed" value={fmtDateShort(order.completion_date)} />
      </Row>
      {Array.isArray(order.orders_jobs) && order.orders_jobs.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {order.orders_jobs.slice().sort((a: any, b: any) => (a.priority ?? 99) - (b.priority ?? 99)).map((oj: any, i: number) => (
            <span key={oj.id ?? i} style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: C.brandLight, border: `1px solid ${C.accent}22`,
              borderRadius: 4, padding: "2px 6px", fontSize: 10, color: C.brand, fontWeight: 600,
            }}>
              #{oj.priority} {oj.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Job Progress Section ─────────────────────────────────────────────────────
function JobProgressInfo({ done, expected }: { done: any[]; expected: any[] }) {
  const safeDone = Array.isArray(done) ? done : [];
  const safeExpected = Array.isArray(expected) ? expected : [];
  if (!safeDone.length && !safeExpected.length) return <span style={{ fontSize: 11, color: C.textLight }}>No jobs tracked</span>;

  const doneIds = new Set(safeDone.map((j: any) => j.done_id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {safeExpected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {safeExpected.map((j: any, i: number) => {
            const isDone = doneIds.has(j.done_id);
            return (
              <div key={j.id ?? i} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: isDone ? C.successBg : C.neutralBg,
                border: `1px solid ${isDone ? C.success + "33" : C.border}`,
                borderRadius: 4, padding: "2px 6px", fontSize: 10,
              }}>
                <span>{isDone ? "✓" : "○"}</span>
                <span style={{ fontWeight: 600, color: isDone ? C.success : C.textMid }}>
                  {(j.done ?? "").replace("_done", "").replace(/_/g, " ")}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {safeDone.length > 0 && safeExpected.length === 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {safeDone.map((j: any, i: number) => (
            <Badge key={j.id ?? i} label={(j.done ?? `#${j.done_id}`).replace("_done", "")} bg={C.successBg} color={C.success} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Repairs Section ──────────────────────────────────────────────────────────
function RepairsInfo({ repairs }: { repairs: any[] }) {
  const allBins: any[] = (Array.isArray(repairs) ? repairs : []).flatMap((rep: any) => {
    const rj = rep?.repair_jobs;
    if (!rj) return [];
    return Array.isArray(rj) ? rj : [rj];
  });
  if (!allBins.length) return <span style={{ fontSize: 11, color: C.textLight }}>No repair bins</span>;

  const sorted = [...allBins].sort((a, b) => {
    const aD = a.status_id === 4 ? 1 : 0;
    const bD = b.status_id === 4 ? 1 : 0;
    if (aD !== bD) return aD - bD;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {sorted.slice(0, 5).map((rj: any, idx: number) => {
        const sc = statusColor(rj?.status);
        const tech = rj?.technician;
        const jobs: any[] = Array.isArray(rj?.jobs_assigned) ? rj.jobs_assigned : [];
        return (
          <div key={rj?.id ?? idx} style={{ border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden" }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: 4, padding: "6px 8px",
              background: rj?.status?.toLowerCase().includes("complet") ? C.successBg + "66" : C.bg,
              borderBottom: `1px solid ${C.border}`,
            }}>
              <span style={{ fontWeight: 700, fontSize: 11, color: C.text }}>Bin #{rj?.id}</span>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {rj?.status && <Badge label={rj.status} {...sc} />}
                {tech?.name && <span style={{ fontSize: 10, color: C.textLight }}>{tech.name}</span>}
              </div>
            </div>
            {jobs.length > 0 && (
              <div style={{ padding: "6px 8px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                {jobs.map((ja: any, i: number) => (
                  <span key={ja?.id ?? i} style={{
                    background: C.brandLight, borderRadius: 4, padding: "2px 6px",
                    fontSize: 10, fontWeight: 600, color: C.brand,
                  }}>
                    {ja?.enum_jobs?.name ?? `Job #${ja?.job_id}`}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {sorted.length > 5 && (
        <span style={{ fontSize: 10, color: C.textLight }}>+{sorted.length - 5} more bins...</span>
      )}
    </div>
  );
}

// ─── Reports Section ──────────────────────────────────────────────────────────
function ReportsInfo({ reports }: { reports: any[] }) {
  const safe = Array.isArray(reports) ? reports : [];
  if (!safe.length) return <span style={{ fontSize: 11, color: C.textLight }}>No reports</span>;

  // Split into normal and damage reports
  const normalReports = safe.filter(r => r.status_id !== 12);
  const damageReports = safe.filter(r => r.status_id === 12);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {normalReports.slice(0, 3).map((r: any, i: number) => {
        const sc = statusColor(r?.enum_status?.name);
        return (
          <div key={r?.id ?? i} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 11, color: C.text }}>Report #{r?.id}</span>
              <div style={{ display: "flex", gap: 4 }}>
                {r?.enum_damages?.name && <Badge label={r.enum_damages.name} bg={C.dangerBg} color={C.danger} />}
                {r?.enum_status?.name && <Badge label={r.enum_status.name} {...sc} />}
              </div>
            </div>
            <Row>
              <KV label="Reporter" value={r?.reporter?.name} />
              <KV label="Causer" value={r?.causer?.name} />
              <KV label="Created" value={fmtDateShort(r?.created_at)} />
            </Row>
          </div>
        );
      })}
      {damageReports.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: C.danger, textTransform: "uppercase" }}>Damage Reports ({damageReports.length})</span>
          {damageReports.slice(0, 2).map((r: any, i: number) => (
            <div key={r?.id ?? i} style={{ marginTop: 4, padding: "4px 8px", background: C.dangerBg + "66", borderRadius: 4 }}>
              <span style={{ fontSize: 10, color: C.danger, fontWeight: 600 }}>{r?.enum_damages?.name}</span>
              <span style={{ fontSize: 10, color: C.textLight, marginLeft: 8 }}>by {r?.causer?.name ?? "unknown"}</span>
            </div>
          ))}
        </div>
      )}
      {safe.length > 3 && (
        <span style={{ fontSize: 10, color: C.textLight }}>+{safe.length - 3} more reports...</span>
      )}
    </div>
  );
}

// ─── Grades Section ───────────────────────────────────────────────────────────
function GradesInfo({ grades }: { grades: any[] }) {
  const safe = Array.isArray(grades) ? grades : [];
  if (!safe.length) return <span style={{ fontSize: 11, color: C.textLight }}>No grades</span>;

  const gradeColors: Record<string, { bg: string; color: string }> = {
    "A/B": { bg: "#00ff00", color: "#000" },
    "C1": { bg: "#ff0000", color: "#fff" },
    "C2": { bg: "#ff0000", color: "#fff" },
    "C3": { bg: "#ff0000", color: "#fff" },
    "NG": { bg: "#000000", color: "#fff" },
    "Trading": { bg: "#a85f00", color: "#fff" },
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {safe.map((g: any, i: number) => {
        const label = (g?.name ?? `Grade #${g?.id}`).replace(" Work", "");
        const gc = gradeColors[label] ?? { bg: C.brandLight, color: C.brand };
        return (
          <div key={g?.id ?? i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{
              background: gc.bg, color: gc.color,
              fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
            }}>
              {label}
            </span>
            <span style={{ fontSize: 9, color: C.textLight }}>{fmtDateShort(g?.created_at)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Parts Section ────────────────────────────────────────────────────────────
function PartsInfo({ parts }: { parts: any[] }) {
  const safe = Array.isArray(parts) ? parts : [];
  if (!safe.length) return <span style={{ fontSize: 11, color: C.textLight }}>No parts queued</span>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {safe.slice(0, 3).map((p: any, i: number) => {
        const inv = p?.parts_inventory;
        return (
          <div key={p?.id ?? i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: i < Math.min(safe.length, 3) - 1 ? `1px solid ${C.border}` : "none" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{inv?.part_name ?? `Part #${p?.id}`}</span>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {inv?.price != null && <Badge label={`$${inv.price.toFixed(2)}`} bg={C.successBg} color={C.success} />}
              <span style={{ fontSize: 10, color: C.textLight }}>{p?.technicians?.name}</span>
            </div>
          </div>
        );
      })}
      {safe.length > 3 && (
        <span style={{ fontSize: 10, color: C.textLight }}>+{safe.length - 3} more parts...</span>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function PhoneResultCard({ phone }: { phone: PhoneData }) {
  const [expanded, setExpanded] = useState(true);
  
  const order = Array.isArray(phone.orders) ? phone.orders[0] : phone.orders;
  const repairsCount = (phone.repairs ?? []).flatMap((r: any) => r?.repair_jobs ?? []).length;
  const reportsCount = (phone.reports ?? []).length;
  const gradesCount = (phone.phone_grades ?? []).length;
  const partsCount = (phone.parts_queue ?? []).length;

  return (
    <div style={{
      background: C.card,
      border: `1px solid ${C.borderDark}`,
      borderRadius: 10,
      overflow: "hidden",
      fontSize: 12,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}>
      {/* Header */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
          background: C.brand,
          border: "none",
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>📱</span>
          <div style={{ textAlign: "left" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>
                {phone.model ?? "Unknown Model"}
              </span>
              {phone.is_active !== null && (
                <span style={{ fontSize: 14 }}>{phone.is_active ? "💚" : "🪦"}</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 10 }}>ID: {phone.id}</span>
              <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{phone.imei}</span>
            </div>
          </div>
        </div>
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</span>
      </button>

      {expanded && (
        <div>
          {/* Basic info row */}
          <div style={{ padding: "10px 12px", background: C.bg, borderBottom: `1px solid ${C.border}` }}>
            <Row>
              <KV label="Group #" value={phone.group_number} />
              <KV label="Mark" value={phone.mark} />
              <KV label="Paint color" value={phone.paint_color} />
              <KV label="Original box" value={phone.original_box != null ? (phone.original_box ? "Yes" : "No") : null} />
              <KV label="Pending" value={phone.pending != null ? (phone.pending ? "Yes" : "No") : null} />
              <KV label="Sent out" value={phone.sent_out != null ? (phone.sent_out ? "Yes" : "No") : null} />
              <KV label="Scanned" value={fmtDateShort(phone.date_scanned)} />
            </Row>
          </div>

          {/* Order */}
          <Section title="Order" defaultOpen={true}>
            <OrderInfo order={order} />
          </Section>

          {/* Job Progress */}
          <Section title="Job Progress" defaultOpen={true}>
            <JobProgressInfo done={phone.phone_jobs_done ?? []} expected={phone.phone_jobs_expected ?? []} />
          </Section>

          {/* Repair Bins */}
          <Section title="Repair Bins" count={repairsCount}>
            <RepairsInfo repairs={phone.repairs ?? []} />
          </Section>

          {/* Reports */}
          <Section title="Reports" count={reportsCount}>
            <ReportsInfo reports={phone.reports ?? []} />
          </Section>

          {/* Grades */}
          <Section title="Grades" count={gradesCount}>
            <GradesInfo grades={phone.phone_grades ?? []} />
          </Section>

          {/* Parts */}
          {partsCount > 0 && (
            <Section title="Parts Queue" count={partsCount}>
              <PartsInfo parts={phone.parts_queue ?? []} />
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

export default PhoneResultCard;
